import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api';

function getTodayCT() {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const y = parts.find(p => p.type === 'year').value;
  const m = parts.find(p => p.type === 'month').value;
  const d = parts.find(p => p.type === 'day').value;
  return `${y}-${m}-${d}`;
}

const STATUS_COLORS = {
  submitted: '#3b82f6',
  flagged: '#ef4444',
  reviewed: '#10b981',
};

const STATUS_LABELS = {
  submitted: 'Submitted',
  flagged: 'Flagged',
  reviewed: 'Reviewed',
};

export default function InspectionListPage() {
  const navigate = useNavigate();
  const today = getTodayCT();

  const [filters, setFilters] = useState({
    date_from: today,
    date_to: today,
    driver_id: '',
    truck_id: '',
    shift_type: '',
    status: '',
  });
  const [inspections, setInspections] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [drivers, setDrivers] = useState([]);
  const [trucks, setTrucks] = useState([]);

  // Load filter options
  useEffect(() => {
    api.getDrivers().then(data => {
      setDrivers(Array.isArray(data) ? data : (data.drivers || []));
    }).catch(() => {});
    api.getTrucks().then(data => {
      setTrucks(Array.isArray(data) ? data : (data.trucks || []));
    }).catch(() => {});
  }, []);

  const doSearch = useCallback(async (pageNum, searchFilters) => {
    setLoading(true);
    setError('');
    try {
      const queryParams = { ...searchFilters, page: pageNum, page_size: 50 };
      // Build query string
      const params = new URLSearchParams();
      for (const [k, v] of Object.entries(queryParams)) {
        if (v !== undefined && v !== null && v !== '') params.set(k, v);
      }
      const qs = params.toString() ? '?' + params.toString() : '';
      const data = await api.getInspections(qs);

      if (data && data.pagination) {
        setInspections(data.data || data.inspections || []);
        setPagination(data.pagination);
        setPage(data.pagination.page);
      } else {
        const list = Array.isArray(data) ? data : (data.inspections || data.data || []);
        setInspections(list);
        setPagination(null);
      }
    } catch (err) {
      setError(err.message || 'Failed to load inspections');
    }
    setLoading(false);
  }, []);

  // Auto-search on load
  useEffect(() => {
    doSearch(1, filters);
  }, []); // eslint-disable-line

  function handleSearch() {
    setPage(1);
    doSearch(1, filters);
  }

  function handleFilterChange(e) {
    setFilters(f => ({ ...f, [e.target.name]: e.target.value }));
  }

  function handlePageChange(newPage) {
    doSearch(newPage, filters);
  }

  function getDriverName(inspection) {
    return inspection.driver_name || inspection.driver_full_name || '—';
  }

  function getTruckUnit(inspection) {
    return inspection.truck_unit_number || inspection.unit_number || '—';
  }

  function getResultsSummary(inspection) {
    if (inspection.pass_count !== undefined && inspection.total_count !== undefined) {
      return `${inspection.pass_count}/${inspection.total_count} Pass`;
    }
    if (inspection.fail_count !== undefined) {
      return inspection.fail_count > 0 ? `${inspection.fail_count} Failed` : 'All Pass';
    }
    return '—';
  }

  function formatInspectionDate(dateStr) {
    if (!dateStr) return '—';
    try {
      const d = new Date(dateStr.includes('T') ? dateStr : dateStr.replace(' ', 'T') + 'Z');
      return d.toLocaleDateString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr;
    }
  }

  function formatSubmittedAt(dateStr) {
    if (!dateStr) return '—';
    try {
      const d = new Date(dateStr.includes('T') ? dateStr : dateStr.replace(' ', 'T') + 'Z');
      return d.toLocaleString('en-US', {
        timeZone: 'America/Chicago',
        month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  }

  return (
    <div className="inspection-list">
      <div className="inspection-list__header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <h1>Inspection Reports</h1>
        <button type="button" className="btn btn-outline" onClick={() => navigate('/maintenance/inspections')}>
          ← Bucket
        </button>
      </div>

      {/* Filters */}
      <div className="inspection-list__filters">
        <div className="form-group">
          <label>From</label>
          <input
            type="date"
            name="date_from"
            value={filters.date_from}
            onChange={handleFilterChange}
            className="form-input"
          />
        </div>
        <div className="form-group">
          <label>To</label>
          <input
            type="date"
            name="date_to"
            value={filters.date_to}
            onChange={handleFilterChange}
            className="form-input"
          />
        </div>
        <div className="form-group">
          <label>Driver</label>
          <select name="driver_id" value={filters.driver_id} onChange={handleFilterChange} className="form-input">
            <option value="">All Drivers</option>
            {drivers.map(d => (
              <option key={d.id} value={d.id}>{d.full_name || d.username}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>Truck</label>
          <select name="truck_id" value={filters.truck_id} onChange={handleFilterChange} className="form-input">
            <option value="">All Trucks</option>
            {trucks.map(t => (
              <option key={t.id} value={t.id}>{t.unit_number}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>Shift</label>
          <select name="shift_type" value={filters.shift_type} onChange={handleFilterChange} className="form-input">
            <option value="">All Shifts</option>
            <option value="day">Day</option>
            <option value="night">Night</option>
          </select>
        </div>
        <div className="form-group">
          <label>Status</label>
          <select name="status" value={filters.status} onChange={handleFilterChange} className="form-input">
            <option value="">All</option>
            <option value="submitted">Submitted</option>
            <option value="flagged">Flagged</option>
            <option value="reviewed">Reviewed</option>
          </select>
        </div>
        <div className="form-group" style={{ alignSelf: 'flex-end' }}>
          <button className="btn btn-gold" onClick={handleSearch}>Search</button>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Table */}
      {loading ? (
        <div className="loading-spinner">Loading inspections...</div>
      ) : (
        <div className="table-responsive">
          <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Shift</th>
                <th>Driver</th>
                <th>Truck</th>
                <th>Odometer</th>
                <th>Results</th>
                <th>Status</th>
                <th>Submitted</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {inspections.length === 0 ? (
                <tr><td colSpan="9" className="text-center">No inspections found.</td></tr>
              ) : inspections.map(insp => (
                <tr
                  key={insp.id}
                  className={insp.status === 'flagged' ? 'inspection-list__row--flagged' : ''}
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/maintenance/inspections/${insp.id}`)}
                >
                  <td>{formatInspectionDate(insp.inspection_date || insp.created_at)}</td>
                  <td style={{ textTransform: 'capitalize' }}>{insp.shift_type || '—'}</td>
                  <td>{getDriverName(insp)}</td>
                  <td><strong>{getTruckUnit(insp)}</strong></td>
                  <td>{insp.odometer ? Number(insp.odometer).toLocaleString() : '—'}</td>
                  <td>{getResultsSummary(insp)}</td>
                  <td>
                    <span
                      className="status-badge"
                      style={{
                        backgroundColor: STATUS_COLORS[insp.status] || '#6b7280',
                        color: '#fff',
                      }}
                    >
                      {STATUS_LABELS[insp.status] || insp.status}
                    </span>
                  </td>
                  <td>{formatSubmittedAt(insp.submitted_at || insp.created_at)}</td>
                  <td>
                    <button
                      className="btn btn-sm btn-outline"
                      onClick={(e) => { e.stopPropagation(); navigate(`/maintenance/inspections/${insp.id}`); }}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.total_pages > 1 && (
        <div className="inspection-list__pagination">
          <button
            className="btn btn-sm btn-outline"
            disabled={page <= 1}
            onClick={() => handlePageChange(page - 1)}
          >
            &laquo; Previous
          </button>
          <span className="inspection-list__page-info">
            Page {page} of {pagination.total_pages} ({pagination.total} total)
          </span>
          <button
            className="btn btn-sm btn-outline"
            disabled={page >= pagination.total_pages}
            onClick={() => handlePageChange(page + 1)}
          >
            Next &raquo;
          </button>
        </div>
      )}
    </div>
  );
}
